/**
 * Constantes de estilo e configuração para ExportModal V3
 */

export const MODAL_DIMENSIONS = {
  maxWidth: 'max-w-[320px]',
  maxHeight: 'max-h-[90vh]',
} as const;

export const MODAL_STYLES = {
  overlay: 'fixed inset-0 bg-navy-900/80 backdrop-blur-sm flex items-center justify-center z-50 p-3',
  container: `bg-navy-800 rounded-lg w-full ${MODAL_DIMENSIONS.maxWidth} animate-fadeIn ${MODAL_DIMENSIONS.maxHeight} flex flex-col shadow-xl border border-navy-700/50`,
  header: 'flex justify-between items-center p-3 border-b border-navy-700',
  headerTitle: 'text-sm font-semibold text-white truncate',
  closeButton: 'text-gray-400 hover:text-white transition-colors p-1 rounded-lg hover:bg-navy-700/50',
  content: 'p-3 space-y-3 overflow-auto flex-1 min-h-0',
  footer: 'p-3 border-t border-navy-700 bg-navy-800 flex flex-col gap-2',
  buttonPrimary: 'px-3 py-2 text-sm rounded bg-yellow-400 hover:bg-yellow-500 text-navy-900 font-medium disabled:opacity-60 disabled:cursor-not-allowed transition-colors w-full',
  buttonSecondary: 'px-3 py-2 text-sm bg-navy-700 hover:bg-navy-600 text-gray-200 rounded disabled:opacity-60 disabled:cursor-not-allowed transition-colors w-full',
  select: 'px-2 py-1.5 rounded bg-navy-700 text-gray-200 text-sm border border-navy-600 hover:border-navy-500 focus:outline-none focus:ring-1 focus:ring-yellow-400/50 transition-colors w-full',
  badge: 'px-2.5 py-1 rounded-md text-sm font-medium',
  badgeRecommended: 'bg-yellow-400 text-navy-900',
  badgeNeutral: 'bg-navy-700 text-gray-300',
} as const;

export const EXPORT_FORMATS: Record<string, string> = {
  tiff: 'TIFF',
  png: 'PNG',
  pdf: 'PDF',
  eps: 'EPS',
} as const;

export const EXPORT_MODES: Record<string, string> = {
  batch: 'Batch (padrão)',
  simple: 'Simple',
} as const;

export const EXPORT_FORMAT_DESCRIPTIONS: Record<string, string> = {
  tiff: 'Máxima qualidade - Padrão para produção',
  png: 'Pré-visualização e layout',
  pdf: 'Aprovação e arquivo',
  eps: 'Compatibilidade legado',
} as const;

// Esquema de campos por formato para renderização dinâmica
export const FORMAT_FIELD_SCHEMAS: Record<string, Array<{
  key: string;
  label: string;
  type: 'number' | 'boolean' | 'string' | 'select';
  min?: number;
  max?: number;
  step?: number;
  options?: Array<{ value: string; label: string }>;
  note?: string;
}>> = {
  png: [
    { key: 'dpix', label: 'DPI X', type: 'number', min: 72, max: 1200, step: 1 },
    { key: 'dpiy', label: 'DPI Y', type: 'number', min: 72, max: 1200, step: 1 },
    { key: 'transparent', label: 'Fundo transparente', type: 'boolean' },
    { key: 'color_mode', label: 'Modo de cor', type: 'select', options: [
      { value: 'RGB', label: 'RGB' }, { value: 'CMYK', label: 'CMYK' }, { value: 'Grayscale', label: 'Grayscale' }
    ]},
    { key: 'bit_depth', label: 'Profundidade de bits', type: 'number', min: 1, max: 64 },
    { key: 'antialias', label: 'Antialias', type: 'select', options: [
      { value: 'none', label: 'None' }, { value: 'normal', label: 'Normal' }, { value: 'super', label: 'Super' }
    ]},
    { key: 'interlaced', label: 'Interlaced', type: 'boolean' },
    { key: 'background_color', label: 'Cor de fundo (hex)', type: 'string', note: '#RRGGBB' },
    { key: 'embed_icc_profile', label: 'Incorporar ICC', type: 'boolean' },
    { key: 'use_color_profile', label: 'Usar perfil de cor', type: 'boolean' },
  ],
  tiff: [
    { key: 'dpix', label: 'DPI X', type: 'number', min: 72, max: 1200 },
    { key: 'dpiy', label: 'DPI Y', type: 'number', min: 72, max: 1200 },
    { key: 'color_mode', label: 'Modo de cor', type: 'select', options: [
      { value: 'RGB', label: 'RGB' }, { value: 'CMYK', label: 'CMYK' }, { value: 'Grayscale', label: 'Grayscale' }
    ]},
    { key: 'bits_per_channel', label: 'Bits por canal', type: 'number', min: 1, max: 16 },
    { key: 'compression', label: 'Compressão', type: 'select', options: [
      { value: 'None', label: 'None' }, { value: 'LZW', label: 'LZW' }, { value: 'ZIP', label: 'ZIP' }, { value: 'JPEG', label: 'JPEG' }
    ]},
    { key: 'transparent', label: 'Transparente', type: 'boolean' },
    { key: 'black_overprint', label: 'Overprint preto', type: 'boolean' },
    { key: 'embed_icc_profile', label: 'Incorporar ICC', type: 'boolean' },
    { key: 'icc_profile_name', label: 'ICC (nome)', type: 'string' },
    { key: 'preserve_original_size', label: 'Preservar tamanho original', type: 'boolean' },
  ],
  pdf: [
    { key: 'export_range', label: 'Faixa de exportação', type: 'select', options: [
      { value: 'selection', label: 'Selection' }, { value: 'page', label: 'Page' }, { value: 'document', label: 'Document' }
    ]},
    { key: 'page_size_mode', label: 'Modo de tamanho de página', type: 'select', options: [
      { value: 'objects', label: 'Objects' }, { value: 'page', label: 'Page' }
    ]},
    { key: 'crop_to_export_area', label: 'Recortar à área de exportação', type: 'boolean' },
    { key: 'preset', label: 'Preset PDF', type: 'string' },
    { key: 'compatibility', label: 'Compatibilidade', type: 'string' },
    { key: 'output_color', label: 'Modo de cor', type: 'select', options: [
      { value: 'RGB', label: 'RGB' }, { value: 'CMYK', label: 'CMYK' }, { value: 'Grayscale', label: 'Grayscale' }, { value: 'native', label: 'Nativo' }
    ]},
    { key: 'bitmap_compression', label: 'Compressão bitmap', type: 'select', options: [
      { value: 'None', label: 'None' }, { value: 'LZW', label: 'LZW' }, { value: 'JPEG', label: 'JPEG' }, { value: 'ZIP', label: 'ZIP' }, { value: 'JP2', label: 'JPEG 2000' }
    ]},
    { key: 'embed_fonts', label: 'Incorporar fontes', type: 'boolean' },
    { key: 'subset_fonts_percent', label: 'Subset fontes (%)', type: 'number', min: 0, max: 100 },
    { key: 'embed_icc_profile', label: 'Incorporar ICC', type: 'boolean' },
    { key: 'icc_profile_name', label: 'ICC (nome)', type: 'string' },
    { key: 'preserve_overprints', label: 'Preservar overprints', type: 'boolean' },
    { key: 'convert_spot_to_process', label: 'Spot → Process', type: 'boolean' },
    { key: 'author', label: 'Autor', type: 'string' },
    { key: 'optimize_for_web', label: 'Otimizar para web', type: 'boolean' },
    { key: 'include_comments', label: 'Incluir comentários', type: 'boolean' },
    { key: 'encoding', label: 'Codificação', type: 'select', options: [
      { value: 'binary', label: 'Binary' }, { value: 'ascii', label: 'ASCII85' }
    ]},
    { key: 'jpeg_quality_int', label: 'Qualidade JPEG', type: 'number', min: 0, max: 100 },
    { key: 'downsample_color', label: 'Downsample (cor)', type: 'number', min: 0, max: 1200 },
    { key: 'downsample_grayscale', label: 'Downsample (escala cinza)', type: 'number', min: 0, max: 1200 },
    { key: 'downsample_monochrome', label: 'Downsample (mono)', type: 'number', min: 0, max: 2400 },
    { key: 'render_complex_fills', label: 'Render fills complexos', type: 'boolean' },
    { key: 'compress_text_graphics', label: 'Comprimir texto/gráficos', type: 'boolean' },
    { key: 'export_text_as_curves', label: 'Exportar texto como curvas', type: 'boolean' },
  ],
  eps: [
    { key: 'dpix', label: 'DPI X', type: 'number', min: 72, max: 1200, step: 1 },
    { key: 'dpiy', label: 'DPI Y', type: 'number', min: 72, max: 1200, step: 1 },
    { key: 'color_mode', label: 'Modo de cor', type: 'select', options: [
      { value: 'CMYK', label: 'CMYK' }, { value: 'RGB', label: 'RGB' }, { value: 'Grayscale', label: 'Grayscale' }, { value: 'native', label: 'Nativo' }
    ]},
    { key: 'preview', label: 'Tipo de preview', type: 'select', options: [
      { value: 'none', label: 'Nenhum' }, { value: 'tiff', label: 'TIFF' }, { value: 'wmf', label: 'WMF' }
    ]},
    { key: 'preview_mode', label: 'Preview mode', type: 'select', options: [
      { value: 'bit8', label: '8-bit' }, { value: 'bit24', label: '24-bit' }
    ]},
    { key: 'export_text_as', label: 'Exportar texto como', type: 'select', options: [
      { value: 'text', label: 'Texto' }, { value: 'curves', label: 'Curvas' }
    ]},
    { key: 'include_fonts', label: 'Incorporar fontes', type: 'boolean' },
    { key: 'eps_level', label: 'EPS level', type: 'select', options: [
      { value: 'PostScript2', label: 'PostScript 2' }, { value: 'PostScript3', label: 'PostScript 3' }
    ]},
    { key: 'convert_spot_to_process', label: 'Spot → Process', type: 'boolean' },
    { key: 'transparent', label: 'Fundo transparente', type: 'boolean' },
    { key: 'overprints', label: 'Overprints', type: 'boolean' },
    { key: 'black_overprint', label: 'Overprint preto', type: 'boolean' },
    { key: 'author', label: 'Autor', type: 'string' },
    { key: 'jpeg_compression', label: 'JPEG compressão', type: 'boolean' },
    { key: 'jpeg_quality_eps', label: 'Qualidade JPEG', type: 'number', min: 0, max: 12 },
    { key: 'auto_trapping', label: 'Auto trapping', type: 'boolean' },
    { key: 'bounding_box', label: 'Bounding box', type: 'select', options: [
      { value: 'objects', label: 'Objects' }, { value: 'page', label: 'Page' }
    ]},
    { key: 'bleed_limit_mm', label: 'Bleed (mm)', type: 'number', min: 0, max: 10, step: 0.001 },
    { key: 'floating_point_output', label: 'Ponto flutuante', type: 'boolean' },
    { key: 'maintain_opi_links', label: 'Manter OPI links', type: 'boolean' },
    { key: 'auto_increase_gradient_steps', label: 'Auto steps gradiente', type: 'boolean' },
  ]
};

